import { html, css, nothing, PropertyValues } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { choose } from 'lit/directives/choose.js';
import { consume } from '@lit/context';
import { publicLedgerServiceContext } from '../../../../ns-services/contexts.js';

import { ButtressEntity } from '@buttress/crag';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import { PublicLedgerService } from '../../../../ns-services/ns-public-ledger-service.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Job from '../../../../models/job.js';
import type Person from '../../../../models/person.js';

import {
  PublicLedgerMessageType,
  PublicLedgerMessage,
  PublicLedgerTransaction,
} from '../../../../models/public-ledger.js';
import { callLambda, lambdaErrorCode } from '../../../../helpers/lambda.js';
import { personDisplayName } from '../../../../helpers/person.js';
import { renderDate } from '../../../../helpers/format-render.js';
import {
  collectSignatureState,
  ledgerResultText,
  ruleSignedUnder,
  signingGroup,
  type CollectSignatureView,
} from '../../../../helpers/collect-signature-state.js';
import { getMessageContent } from './tree-utils.js';
import { groupsOfSigner, ruleFromRequirement, summariseRule, type RuleGroup } from './signatory-change.js';
import type Company from '../../../../models/company.js';
import type { PublicLedgerTopic } from '../../../../models/public-ledger.js';
import { createLogger } from '../../../../ns-services/logger.js';
import '../../../../ns-elements/ns-job-layout.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/textarea/textarea.js';
import definition from '@job-schemas/public-ledger/collect-signature.json';
import '../../../../ns-elements/ns-detail-list.js';
import {
  AUTOPAY_CODES,
  FEE_WAITING_FOR_TOP_UP,
  approveSummaryRows,
  approveSignRefusal,
  resignChanges,
  RESIGN_CHANGED_REFUSAL,
  APPROVE_RATE_NOTE,
  type AutoPaymentRow,
  type ProposedSettings,
} from '../../../../helpers/auto-payments.js';
import { prepareApproveSignature, submitApproveSignature } from '../../../../helpers/auto-payments-api.js';
import { CHANGE_AMOUNTS_SIGN_COPY, nowAfterRateNote, nowAfterRows } from '../../../../helpers/auto-payment-changes.js';
import {
  CARD_CHANGE_GONE_COPY,
  cardChangeSignCopy,
  cardChangeSignRefusal,
} from '../../../../helpers/auto-payment-card-change.js';
import {
  STOP_GONE_COPY,
  stopConsequences,
  stopSignCopy,
  stopSignRefusal,
} from '../../../../helpers/auto-payment-stop.js';
import { readingCredits } from '../../../../helpers/public-ledger-credits.js';
import { cardDots } from '../../../../helpers/auto-payments.js';
import { NO_HISTORY_REFUSAL } from '../../../../helpers/public-ledger-mirror.js';
import { readOperatingAnchor } from '../../../../helpers/public-ledger-mirror-api.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';
import type { ShownOnCard } from '../../../../helpers/public-ledger-sign-check.js';
import { jobSchema } from '../../job-schema.js';

const logger = createLogger('collect-public-ledger-signature');

const enum SignatureType {
  NOT_IMPLEMENTED,
  ACCOUNT_CHANGE,
  TOPIC_CHANGE,
  MESSAGE_SUBMIT,
  APPROVE_ALLOWANCE,
  // #25 phase 6 (round 15): a change of the automatic payment card, signed as an approval is
  CARD_CHANGE,
}

// How many times a signature is prepared again, without asking, when the signing window closed or the ledger was busy
const APPROVE_SIGN_ATTEMPTS = 3;

const PROCESSED_STATES: CollectSignatureView['state'][] = [
  'PROCESSED_SUBMITTED',
  'PROCESSED_REJECTED',
  'PROCESSED_ENDED',
];

/**
 * A signatory's card for one public ledger transaction (or every part of a long message, signed with one press). The
 * signatory signs or rejects it with a reason; one rejection ends the signing for everyone. Once the transaction has gone
 * to the public ledger, been rejected or ended, the card shows that and stays open until the signatory completes it.
 *
 * A change to who signs the company's topic (TOPIC_CHANGE, #38) is approved rather than signed: nothing goes on the
 * public ledger from this card, so it never touches the wallet or the device, and nodeStream applies the change once both
 * rules have enough approvals.
 */
@customElement(NSCollectPublicLedgerSignature.is)
export default class NSCollectPublicLedgerSignature extends NSJobComponent {
  static get is() {
    return 'collect-public-ledger-signature';
  }

  @consume({ context: publicLedgerServiceContext })
  private __plLedgerService?: PublicLedgerService;

  @state()
  private __signatureType: SignatureType = SignatureType.NOT_IMPLEMENTED;

  @state()
  private __transaction?: PublicLedgerTransaction;

  // Every transaction this card signs at once (the parts of a long message), the job's own included
  @state()
  private __transactions: PublicLedgerTransaction[] = [];

  @state()
  private __message?: PublicLedgerMessage;

  @state()
  private __messageType: PublicLedgerMessageType = PublicLedgerMessageType.PLAIN_TEXT;

  @state()
  private __messageContent: string[] = [];

  @state()
  private __warning: string | undefined | null = '';

  @state()
  private __processed: string | null = null;

  @state()
  private __rejecting = false;

  @state()
  private __reason = '';

  @state()
  private __reasonError?: string;

  @state()
  private __rejecterName: string | null = null;

  @state()
  private __busy = false;

  // The rule this is signed or approved under now (the company's, or the topic's for a topic message or a topic change)
  // and, for a signatory change, the rule proposed; with the names of the people they name
  @state()
  private __currentRule: RuleGroup | null = null;

  @state()
  private __proposedRule: RuleGroup | null = null;
  // What the card shows, as first read: signing checks every part says exactly this (#24). Signing waits until it is kept
  private __shown: ShownOnCard = { kind: null, message: null, proposedKey: null };
  private __shownMessage: ShownOnCard['message'] = null;
  private __shownProposedKey: unknown = null;
  @state()
  private __ready = false;

  @state()
  private __people: Person[] = [];

  // An automatic payments approval: the settings as the server stored them, for this transaction only
  @state()
  private __proposal: ProposedSettings | null = null;

  @state()
  private __proposalNote: string | null = null;

  // Round 10: the last signature's fee waits for the operating account's top-up, however it is coming
  // (FEE_TOP_UP_WAITING): the card says so instead of an error, until the signer presses Sign again once told they can
  @state()
  private __feeWaitingForTopUp = false;
  // The live allowance's approval schedule (plAutoPayment.allowance.scheduleId): one more place the browser finds the
  // company's operating account on the public mirror
  private __approvalScheduleId: string | null = null;
  // Whether this approval re-signs the company's missing allowance (pendingChange.kind RESIGN naming it), with the settings
  // unchanged; nodeStream pays it like the set-up (#25 DECISION 4.1, settled 2026-10-27)
  @state()
  private __resign = false;
  // A re-sign whose proposal isn't the live settings (resignChanges): never signed, and never shown as unchanged
  @state()
  private __resignChanged = false;
  // What the approval changes: SETUP, RESIGN or AMOUNTS (#25 phase 6), from pendingChange.kind
  @state()
  private __approveKind: string | null = null;
  // The live settings, beside a change of amounts' proposal in the Now / After table
  @state()
  private __liveSettings: AutoPaymentRow | null = null;
  // A card change's record as the card shows it (round 15): its memo, and who changed the card
  @state()
  private __cardChangeMemo: string | null = null;
  @state()
  private __cardChangeBy: string | null = null;
  // #40: a stop of automatic payments (an approval of 0 that pendingChange {kind STOP} names): its memo, who asked, and
  // what stopping means with the company's figures
  @state()
  private __stopMemo: string | null = null;
  @state()
  private __stopBy: string | null = null;
  @state()
  private __stopLines: string[] = [];

  // The company's public ledger account, which must never pay for a ScheduleSign this card signs
  private __companyAccountId: string | null = null;

  // The company's public ledger network, for the browser's own mirror reads before a re-signed allowance is signed
  private __companyNetwork: string | null = null;

  @state()
  private __view: CollectSignatureView = collectSignatureState({ processed: null, transaction: null, personId: null });

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { signed: { type: 'hidden' }, rejected: { type: 'hidden' }, acknowledged: { type: 'hidden' } },
    });
  }

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .pl-message {
        overflow-wrap: anywhere;
      }

      /* A change of amounts' Now / After table (#25 phase 6): a changed setting's row is tinted, its new value bold */
      .now-after {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--wa-font-size-s);
      }

      .now-after th,
      .now-after td {
        padding: var(--wa-space-2xs) var(--wa-space-xs);
        text-align: start;
        vertical-align: top;
      }

      .now-after thead th {
        font-weight: 600;
      }

      .now-after tbody th {
        font-weight: 500;
      }

      .now-after tr.changed {
        background: var(--wa-color-brand-fill-quiet);
      }

      .now-after tr.changed td:last-child {
        font-weight: 600;
      }

      .signing-warning {
        display: flex;
        align-items: center;
        gap: var(--wa-space-s);
      }

      .rejection-reason {
        overflow-wrap: anywhere;
        white-space: pre-line;
      }

      wa-button img[slot='start'] {
        height: 1.25em;
      }
    `,
  ];

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._directory) throw new Error('Unable to find directory service');

    if (!this._auth) throw new Error('Auth service is required!');

    if (!this.__plLedgerService) throw new Error('Unable to find public ledger service');

    if (!this._job) throw new Error('Unable to find job');
    if (this._job.inputValues.transactionType === 'ACCOUNT_CHANGE') this.__signatureType = SignatureType.ACCOUNT_CHANGE;
    if (this._job.inputValues.transactionType === 'TOPIC_CHANGE') this.__signatureType = SignatureType.TOPIC_CHANGE;
    if (this._job.inputValues.transactionType === 'MESSAGE_SUBMIT') this.__signatureType = SignatureType.MESSAGE_SUBMIT;
    if (this._job.inputValues.transactionType === 'APPROVE_ALLOWANCE')
      this.__signatureType = SignatureType.APPROVE_ALLOWANCE;
    if (this._job.inputValues.transactionType === 'CARD_CHANGE') this.__signatureType = SignatureType.CARD_CHANGE;

    this.__readJob(this._job);

    await this._directory.initialised;
    const person = this._directory.me;
    if (!person) throw new Error('Unable to find a profile for the logged in user');

    this.__transaction = await this.loadInput('plTransaction', 'transactionId');

    const messageId = this.__transaction.consensusService?.messageId;
    if (messageId) {
      this.__message = await this._db.getById('plMessage', messageId);
      if (!this.__message) logger.error(`Could not find message with ID: ${messageId}`);

      if (this.__message) {
        const { text, collection, entityId } = (this.__message.content ?? {}) as Record<string, unknown>;
        const kept = (value: unknown) => (typeof value === 'string' ? value : null);
        this.__shownMessage = {
          id: this.__message.id,
          type: this.__message.type,
          text: kept(text),
          collection: kept(collection),
          entityId: kept(entityId),
        };
      }
      if (this.__message && this.__message.type === 'PLAIN_TEXT') {
        this.__messageContent = [this.__message.content.text as string];
      }
      if (this.__message && this.__message.type === 'HASHED') {
        this.__messageType = PublicLedgerMessageType.HASHED;
      }
      if (this.__message && this.__message.type === 'ENCRYPTED') {
        this.__messageType = PublicLedgerMessageType.ENCRYPTED;
      }
    }

    await this.__loadTransactions();
    await this.__loadRules(this.__transaction.companyId);
    if (this.__signatureType === SignatureType.APPROVE_ALLOWANCE) await this.__loadProposal();
    if (this.__signatureType === SignatureType.CARD_CHANGE) await this.__loadCardChange();

    // The kind of change the card renders, and only what it renders of it
    if (this.__signatureType === SignatureType.ACCOUNT_CHANGE) {
      this.__shown = { kind: 'ACCOUNT_CHANGE', message: null, proposedKey: this.__shownProposedKey };
    } else if (this.__signatureType === SignatureType.MESSAGE_SUBMIT) {
      this.__shown = { kind: 'MESSAGE_SUBMIT', message: this.__shownMessage, proposedKey: null };
    }
    this.__ready = true;

    // Another signatory signing or rejecting, or the status cron ending the transaction, switches an open card
    const jobId = this._job.id;
    this._subscriptions.subscribe(`job.${jobId}.*`, () => {
      const updated = this._db?.get(`job.${jobId}`) as Job | undefined;
      if (updated) this.__readJob(updated);
    });
    for (const transaction of this.__transactions) {
      this._subscriptions.subscribe(`plTransaction.${transaction.id}.*`, () =>
        this.__refreshTransaction(transaction.id),
      );
    }
  }

  // Approval cards for a change to who signs the company's topic (#38): approved in nodeStream, not signed on the ledger
  private get __isApproval() {
    return this.__signatureType === SignatureType.TOPIC_CHANGE;
  }

  // The rules a signatory change moves between, or the rule a message or approval is signed under: a company topic
  // message and a topic change go by the topic's own submit key (#38), the rest by the company account's rule
  private async __loadRules(companyId: string) {
    if (!this._db || !companyId) return;
    const company = (await this._db.getById('company', companyId)) as Company | undefined;
    this.__companyAccountId = company?.ledgerCred?.accountId ?? null;
    this.__companyNetwork = company?.ledgerCred?.network ?? null;
    const transactionType = this._job?.inputValues.transactionType as string | undefined;
    let topicSubmitKey: unknown = null;
    const topicId = this.__transaction?.consensusService?.topicId;
    if (topicId && (transactionType === 'MESSAGE_SUBMIT' || transactionType === 'TOPIC_CHANGE')) {
      const topic = (await this._db.getById('plTopic', topicId)) as PublicLedgerTopic | undefined;
      topicSubmitKey = topic?.submitKey ?? null;
    }
    const rule = ruleSignedUnder(transactionType, {
      companyRule: company?.ledgerCred?.signatureRequirement,
      topicSubmitKey,
    });
    this.__currentRule = ruleFromRequirement(rule as Parameters<typeof ruleFromRequirement>[0]);
    this.__proposedRule = ruleFromRequirement(
      this.__transaction?.proposedKey as Parameters<typeof ruleFromRequirement>[0],
    );
    // A copy of what the Proposed line summarises: the store's row may change under it
    const proposedKey = this.__transaction?.proposedKey;
    this.__shownProposedKey = proposedKey ? JSON.parse(JSON.stringify(proposedKey)) : null;
    const people = await this._db.query('people', { companyId: { $eq: companyId } });
    this.__people = people.results as unknown as Person[];
  }

  private __nameOf = (personId: string) => {
    const person = this.__people.find((p) => p.id === personId);
    return person ? personDisplayName(person) : 'A former employee';
  };

  // "You sign as part of Directors and Finance" for a signer named in named groups of the rule this is signed under
  private __renderSignerGroups() {
    const me = this._directory?.me?.id;
    const groups = me ? groupsOfSigner(this.__currentRule, me) : [];
    if (groups.length === 0) return nothing;
    return html`
      <p>You sign as part of ${groups.join(' and ')}.</p>
    `;
  }

  protected willUpdate(changed: PropertyValues<this>) {
    super.willUpdate(changed);
    this.__view = collectSignatureState({
      processed: this.__processed,
      transaction: this.__transaction,
      transactions: this.__transactions,
      personId: this._directory?.me?.id,
      rejecting: this.__rejecting,
      failureReason: this.__failureReason(),
    });
  }

  protected updated(changed: PropertyValues<this>) {
    super.updated(changed);
    if (this.__view.state === 'PROCESSED_REJECTED') this.__loadRejecterName();
    const processed = PROCESSED_STATES.includes(this.__view.state);
    if (processed && !this.__completionConfirmed) {
      this.__completionConfirmed = true;
      this.confirmCardCompletion();
    }
  }

  // The card asks its container to confirm completion once, when it first shows a processed state
  private __completionConfirmed = false;

  private __readJob(job: Job) {
    const metadata = job.metadata ?? [];
    this.__processed = metadata.find((m) => m.key === 'PROCESSED')?.value ?? null;
    const warning = metadata.find((m) => m.key === 'SIGNATURE_SUBMISSION_WARNING')?.value;
    if (warning) this.__warning = warning;
  }

  // The parts of a long message are signed together; a part signed again is signed on its own
  private async __loadTransactions() {
    const lead = this.__transaction;
    if (!this._db || !lead) return;
    const messageId = lead.consensusService?.messageId;
    let messageTransactions: PublicLedgerTransaction[] = [];
    if (messageId && !lead.replacesTransactionId) {
      const query = await this._db.query('plTransaction', { 'consensusService.messageId': { $eq: messageId } });
      messageTransactions = query.results as unknown as PublicLedgerTransaction[];
    }
    this.__transactions = signingGroup(lead, messageTransactions);
  }

  private __refreshTransaction(id: string) {
    const updated = this._db?.get(`plTransaction.${id}`) as PublicLedgerTransaction | undefined;
    if (!updated) return;
    if (this.__transaction?.id === id) this.__transaction = updated;
    this.__transactions = this.__transactions.map((t) => (t.id === id ? updated : t));
  }

  // Why the part this card signs again failed: as recorded on the message part, or from the public ledger's result code
  private __failureReason(): string | null {
    const transaction = this.__transaction;
    if (!transaction?.replacesTransactionId || typeof transaction.partIndex !== 'number') return null;
    const part = this.__message?.parts?.find((p) => p.index === transaction.partIndex);
    return part?.failureReason ?? null;
  }

  private async __loadRejecterName() {
    const personId = this.__view.rejection?.personId;
    if (!personId || this.__rejecterName !== null || !this._db) return;
    this.__rejecterName = '';
    try {
      const person = (await this._db.getById('people', personId)) as Person | undefined;
      this.__rejecterName = person ? personDisplayName(person) : '';
    } catch (err) {
      logger.warn('Unable to read who rejected the transaction', err);
    }
  }

  private async __verifyHashedData() {
    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this._directory) throw new Error('Missing directory service');
      if (!this._directory.me || !this._directory.me.id) throw new Error('Missing directory service person ID');
      if (!this.__message) throw new Error('Missing message');

      const data = {
        messageId: this.__message.id,
      };
      this.loading('Checking data integrity');
      const output = await callLambda(
        'public-ledger/check-data-integrity',
        'POST',
        this._auth.token,
        JSON.stringify(data),
      );
      this.loaded();
      const { isVerified } = output.res as unknown as { isVerified: Boolean };
      if (isVerified) {
        this.__messageContent = [
          `The ${this.__message.content.collection} entity with ID ${this.__message.content.entityId} matches the public ledger`,
        ];
      } else {
        this.__messageContent = [
          `The ${this.__message.content.collection} entity with ID ${this.__message.content.entityId} does not match the public ledger`,
        ];
      }

      this.__messageType = PublicLedgerMessageType.PLAIN_TEXT;
    } catch (err) {
      this.loaded();
      this.showError(err, 'Unable to check the data against the public ledger');
    }
  }

  private async __decryptMessage() {
    try {
      if (!this._auth) throw new Error('Missing auth service');
      if (!this._directory) throw new Error('Missing directory service');
      if (!this._directory.me || !this._directory.me.id) throw new Error('Missing directory service person ID');
      if (!this.__message) throw new Error('Missing message');

      const data = {
        messageId: this.__message.id,
      };
      this.loading('Decrypting the message');
      const output = await callLambda('public-ledger/decrypt-message', 'POST', this._auth.token, JSON.stringify(data));
      this.loaded();
      const { message } = output.res as unknown as { message: ButtressEntity };
      this.__messageContent = getMessageContent(message);
      this.__messageType = PublicLedgerMessageType.PLAIN_TEXT;
    } catch (err) {
      this.loaded();
      this.showError(err, 'Unable to decrypt the message');
    }
  }

  // The settings an APPROVE_ALLOWANCE signs: the company's stored proposal, only while it names this transaction
  private async __loadProposal() {
    const companyId = this.__transaction?.companyId;
    if (!this._db || !companyId) return;
    try {
      const found = await this._db.query('plAutoPayment', { companyId: { $eq: companyId } });
      const [row] = (found?.results ?? []) as unknown as AutoPaymentRow[];
      this.__approvalScheduleId = row?.allowance?.scheduleId ?? null;
      const pending = row?.pendingChange;
      // #40: a stop. Its row and the pendingChange naming it must agree that it is one, and the card shows its memo
      if (pending?.kind === 'STOP' || this.__transaction?.allowance?.tinybars === '0') {
        this.__approveKind = 'STOP';
        const named = pending?.kind === 'STOP' && pending.transactionId === this.__transaction?.id;
        this.__stopMemo =
          named && this.__transaction?.allowance?.tinybars === '0' ? (this.__transaction.allowance.memo ?? null) : null;
        this.__stopBy = pending?.requestedBy ? this.__nameOf(pending.requestedBy) : null;
        await this.__loadStopFigures(companyId, row);
        return;
      }
      if (pending?.transactionId && pending.transactionId === this.__transaction?.id) {
        this.__proposal = pending.proposed ?? null;
        this.__approveKind = pending.kind ?? null;
        this.__liveSettings = row;
        this.__resign = pending.kind === 'RESIGN';
        this.__resignChanged = this.__resign && resignChanges(pending.proposed, row) !== null;
      } else if (row?.setupState === 'ACTIVE' && row.settingsVersion) {
        this.__proposalNote = 'These settings have been applied. My Account shows them.';
      } else {
        this.__proposalNote = 'These settings are no longer waiting for signatures.';
      }
    } catch (err) {
      logger.warn('Unable to read the proposed settings', err);
    }
  }

  // #40: what stopping means, with the company's balances and card
  private async __loadStopFigures(companyId: string, row: AutoPaymentRow | undefined) {
    try {
      const company = (await this._db?.getById('company', companyId)) as Company | undefined;
      const balances = company?.ledgerCred?.balances;
      this.__stopLines = stopConsequences({
        companyCredits: readingCredits(balances?.company),
        operatingCredits: readingCredits(balances?.operating),
        card: cardDots(row),
      });
    } catch (err) {
      logger.warn('Unable to read the company', err);
    }
  }

  // Round 15: a card change's record as its row names it (the memo, with no card details), and the live allowance's
  // schedule for the history check
  private async __loadCardChange() {
    this.__cardChangeMemo = this.__transaction?.cardChange?.memo ?? null;
    const requestedBy = this.__transaction?.requestedBy;
    this.__cardChangeBy = requestedBy ? this.__nameOf(requestedBy) : null;
    const companyId = this.__transaction?.companyId;
    if (!this._db || !companyId) return;
    try {
      const found = await this._db.query('plAutoPayment', { companyId: { $eq: companyId } });
      const [row] = (found?.results ?? []) as unknown as AutoPaymentRow[];
      this.__approvalScheduleId = row?.allowance?.scheduleId ?? null;
    } catch (err) {
      logger.warn('Unable to read the automatic payments', err);
    }
  }

  // Whether this card signs through the server-built ScheduleSign (an approval, or a card change)
  private get __serverSigned() {
    return (
      this.__signatureType === SignatureType.APPROVE_ALLOWANCE || this.__signatureType === SignatureType.CARD_CHANGE
    );
  }

  /**
   * Signs the automatic payments approval (#25 DECISION 4.7): the server builds a ScheduleSign that nodeStream pays for and
   * answers its body bytes; the public ledger service checks them and the schedule on the public mirror against what this
   * card shows (reporting a mismatch), signs them on this device and sends back only the signature, with this device's
   * proof (#24 phase 3). Bytes expire within minutes, so a closed window or a busy ledger prepares and signs again, a few
   * times, without asking.
   * A signature that may complete a change of amounts needs the approval's run fee in the operating account (#25 phase 6):
   * while it isn't there, prepare-signature or submit-signature answer FEE_TOP_UP_WAITING (round 10) and the card stops at
   * once (the caller shows FEE_WAITING_FOR_TOP_UP); the signer is told when the fee is there and signs with one press.
   * Round 15: a card change's record is signed the same way, against the memo the card shows (cardChangeSignRefusal).
   */
  private async __signApprove() {
    const job = this._job;
    const transaction = this.__transaction;
    const me = this._directory?.me?.id;
    const token = this._auth?.token;
    if (!job?.id || !job.workflow?.id || !transaction || !me || !token) return;
    if (!this.__plLedgerService) throw new Error('Missing public ledger service');
    if (this.__signatureType === SignatureType.CARD_CHANGE) {
      await this.__signCardChange(token, {
        transactionId: transaction.id,
        signatoryId: me,
        workflowId: job.workflow.id,
        jobId: job.id,
      });
      return;
    }
    if (this.__approveKind === 'STOP') {
      await this.__signStop(token, {
        transactionId: transaction.id,
        signatoryId: me,
        workflowId: job.workflow.id,
        jobId: job.id,
      });
      return;
    }
    const shown = this.__proposal;
    if (!shown) throw new Error('The settings are still loading. Please try again in a moment.');
    if (this.__resignChanged) throw new Error(RESIGN_CHANGED_REFUSAL);
    const ids = { transactionId: transaction.id, signatoryId: me, workflowId: job.workflow.id, jobId: job.id };
    const historyOperatingAccountId = await this.__historyOperatingAccount();
    for (let attempt = 1; ; attempt++) {
      try {
        const prepared = await prepareApproveSignature(token, ids);
        // Sent earlier and confirmed since: nothing more to sign
        if (prepared.alreadySigned) return;
        // Never signed unseen: the server must name the stored proposal and it must be what the card shows; changed
        // settings are shown, to be signed only once they have been seen
        const refusal = approveSignRefusal(prepared.proposed, shown);
        if (refusal) {
          if (prepared.proposed) this.__proposal = prepared.proposed;
          throw new Error(refusal);
        }
        await this.__plLedgerService.signApproval(
          {
            workflowId: ids.workflowId,
            jobId: ids.jobId,
            transaction,
            bodyBytes: prepared.bodyBytes,
            shown,
            historyOperatingAccountId,
          },
          ({ signature, device }) =>
            submitApproveSignature(token, {
              ...ids,
              preparedTransactionId: prepared.preparedTransactionId,
              signature,
              device,
            }),
        );
        return;
      } catch (err) {
        if (lambdaErrorCode(err) !== AUTOPAY_CODES.signAgain || attempt >= APPROVE_SIGN_ATTEMPTS) throw err;
      }
    }
  }

  // Round 15: a card change's record, signed as __signApprove signs an approval, never unseen: the memo the server signs
  // for must be the one the card shows
  private async __signCardChange(
    token: string,
    ids: { transactionId: string; signatoryId: string; workflowId: string; jobId: string },
  ) {
    const transaction = this.__transaction;
    const memo = this.__cardChangeMemo;
    if (!transaction || !this.__plLedgerService) return;
    if (!memo) throw new Error('The change is still loading. Please try again in a moment.');
    const historyOperatingAccountId = await this.__historyOperatingAccount();
    for (let attempt = 1; ; attempt++) {
      try {
        const prepared = await prepareApproveSignature(token, ids);
        if (prepared.alreadySigned) return;
        const refusal = cardChangeSignRefusal(prepared.memo, memo);
        if (refusal) throw new Error(refusal);
        await this.__plLedgerService.signCardChange(
          {
            workflowId: ids.workflowId,
            jobId: ids.jobId,
            transaction,
            bodyBytes: prepared.bodyBytes,
            memo,
            historyOperatingAccountId,
          },
          ({ signature, device }) =>
            submitApproveSignature(token, {
              ...ids,
              preparedTransactionId: prepared.preparedTransactionId,
              signature,
              device,
            }),
        );
        return;
      } catch (err) {
        if (lambdaErrorCode(err) !== AUTOPAY_CODES.signAgain || attempt >= APPROVE_SIGN_ATTEMPTS) throw err;
      }
    }
  }

  // #40: a stop, signed as a card change's record is: never unseen, the memo the server signs for must be the one shown
  private async __signStop(
    token: string,
    ids: { transactionId: string; signatoryId: string; workflowId: string; jobId: string },
  ) {
    const transaction = this.__transaction;
    const memo = this.__stopMemo;
    if (!transaction || !this.__plLedgerService) return;
    if (!memo) throw new Error(STOP_GONE_COPY);
    const historyOperatingAccountId = await this.__historyOperatingAccount();
    for (let attempt = 1; ; attempt++) {
      try {
        const prepared = await prepareApproveSignature(token, ids);
        if (prepared.alreadySigned) return;
        const refusal = stopSignRefusal(prepared.memo, memo);
        if (refusal) throw new Error(refusal);
        await this.__plLedgerService.signStop(
          {
            workflowId: ids.workflowId,
            jobId: ids.jobId,
            transaction,
            bodyBytes: prepared.bodyBytes,
            memo,
            historyOperatingAccountId,
          },
          ({ signature, device }) =>
            submitApproveSignature(token, {
              ...ids,
              preparedTransactionId: prepared.preparedTransactionId,
              signature,
              device,
            }),
        );
        return;
      } catch (err) {
        if (lambdaErrorCode(err) !== AUTOPAY_CODES.signAgain || attempt >= APPROVE_SIGN_ATTEMPTS) throw err;
      }
    }
  }

  /**
   * The operating account as the company's own public ledger history shows it (the anchor settled 2026-10-26: nodeStream's
   * servers can't fake it), which the service requires to be the one #24's plOperatingAccount read names. A re-signed
   * allowance (#25 phase 4) and a change of amounts (#25 phase 6) need it, as the company has history by then; a set-up is
   * checked against it too once the company has any history. Null for a set-up with no history. Throws with the reason
   * when the history can't be read or names more than one account.
   */
  private async __historyOperatingAccount(): Promise<string | null> {
    // Anything but a set-up needs the anchor: the company's automatic payments are on, so it has history
    const resign = this.__approveKind !== 'SETUP';
    const companyAccountId = this.__companyAccountId;
    if (!companyAccountId) {
      if (!resign) return null;
      throw new Error("Your company's public ledger account is not known, so this can't be signed.");
    }
    if (!this.__plLedgerService) throw new Error('Missing public ledger service');
    // The same network the schedule check reads (MAINNET in production), never one the database alone names: another
    // network's mirror has no history for the company's account, which would skip this check
    const network = this.__plLedgerService.mirrorNetworkFor(this.__companyNetwork);
    if (!network) throw new Error("Your company's public ledger network is not known.");
    const anchor = await readOperatingAnchor(network, companyAccountId, undefined, this.__approvalScheduleId);
    // A company setting up before it ever topped up has no history to check against
    if (anchor.refusal === NO_HISTORY_REFUSAL && !resign) return null;
    if (anchor.refusal !== null) throw new Error(anchor.refusal);
    return anchor.accountId;
  }

  // Signs every transaction this person still has to sign on this card (each part of a long message), then completes it
  async __sign() {
    if (this.__busy || !this.__ready) return;
    if (!this._job || !this._job.id || !this._job.workflow || !this._job.workflow.id)
      throw new Error('Missing job info in the job card');
    this.clearError();

    const toSign = this.__view.toSign;
    if (toSign.length === 0) {
      this.showError('This is not ready to be signed yet. Please try again in a moment.');
      return;
    }

    this.__busy = true;
    try {
      if (!this.__plLedgerService) throw new Error('Missing public ledger service');

      const lead = this.__transaction ?? this.__transactions.find((t) => t.id === toSign[0].id);
      if (!lead) throw new Error('Missing transaction');

      if (this.__serverSigned) {
        // Never signed unseen: the settings (or the card change's record) must be on the card first
        if (this.__approveKind === 'STOP' && !this.__stopMemo) {
          this.showError(STOP_GONE_COPY);
          return;
        }
        if (
          this.__signatureType === SignatureType.APPROVE_ALLOWANCE &&
          this.__approveKind !== 'STOP' &&
          !this.__proposal
        ) {
          this.showError(this.__proposalNote ?? 'The settings are still loading. Please try again in a moment.');
          return;
        }
        if (this.__signatureType === SignatureType.CARD_CHANGE && !this.__cardChangeMemo) {
          this.showError(CARD_CHANGE_GONE_COPY);
          return;
        }
        this.sign();
        this.__feeWaitingForTopUp = false;
        try {
          await this.__signApprove();
        } catch (err) {
          this.signed();
          // Round 10: the fee waits for the operating account's top-up: said on the card, not as an error
          if (lambdaErrorCode(err) === AUTOPAY_CODES.feeTopUpWaiting) {
            this.__feeWaitingForTopUp = true;
            return;
          }
          // Sent but not yet seen on the public ledger: the card stays open, to be opened again shortly
          if (lambdaErrorCode(err) === AUTOPAY_CODES.beingConfirmed) {
            this.showError(
              lambdaRefusal(err, 'Your signature is still being confirmed. Open this card again shortly.'),
            );
            return;
          }
          throw err;
        }
        this.signed();
        await this.outcome('signed');
        return;
      }

      this.sign();
      // Every part in one call: the server checks them all before any reaches the public ledger
      const output = await this.__plLedgerService.signTransactions(
        this._job.workflow.id,
        this._job.id,
        lead,
        toSign.map((t) => t.id),
        this.__shown,
      );
      if (output !== 'USER_ALREADY_SIGNED' && output !== 'SUCCESS') {
        this.__warning = output;
        await this.updateMetadata('SIGNATURE_SUBMISSION_WARNING', output);
        this.signed();
        return this.fatalError(output);
      }

      this.signed();
      await this.outcome('signed');
    } catch (err: unknown) {
      this.signed();
      this.showError(err, 'Something went wrong');
    } finally {
      this.__busy = false;
    }
  }

  // Approves a change to who signs the company's topic (#38): kept in nodeStream, nothing is signed on the public ledger
  private async __approve() {
    if (this.__busy || !this.__ready) return;
    if (!this._job?.id || !this.__transaction) throw new Error('Missing job info in the job card');
    this.clearError();

    if (this.__view.toSign.length === 0) {
      this.showError('This is not ready to be approved yet. Please try again in a moment.');
      return;
    }

    this.__busy = true;
    this.loading('Approving');
    try {
      if (!this.__plLedgerService) throw new Error('Missing public ledger service');
      await this.__plLedgerService.answerTopicSignatoryChange(this._job.id, this.__transaction.id, 'APPROVE');
      this.loaded();
      await this.outcome('signed');
    } catch (err: unknown) {
      this.loaded();
      this.showError(err, 'Unable to approve this');
    } finally {
      this.__busy = false;
    }
  }

  private __startRejecting() {
    this.clearError();
    this.__reasonError = undefined;
    this.__rejecting = true;
  }

  private __cancelRejecting() {
    this.__rejecting = false;
    this.__reasonError = undefined;
  }

  // One rejection, with its reason, ends the signing for every signatory and every part
  private async __reject() {
    if (this.__busy) return;
    if (!this._job?.id || !this._job.workflow?.id || !this.__transaction) return;
    this.clearError();

    const reason = this.__reason.trim();
    if (!reason) {
      this.__reasonError = 'Please give a reason for rejecting.';
      return;
    }
    this.__reasonError = undefined;

    this.__busy = true;
    this.loading('Rejecting');
    try {
      if (!this.__plLedgerService) throw new Error('Missing public ledger service');
      if (this.__isApproval) {
        await this.__plLedgerService.answerTopicSignatoryChange(this._job.id, this.__transaction.id, 'REJECT', reason);
      } else {
        await this.__plLedgerService.rejectTransaction(
          this._job.workflow.id,
          this._job.id,
          this.__transaction.id,
          reason,
        );
      }
      this.loaded();
      await this.outcome('rejected', { reason });
    } catch (err: unknown) {
      this.loaded();
      this.showError(err, 'Unable to reject this');
    } finally {
      this.__busy = false;
    }
  }

  renderAccountChangeTransaction() {
    return html`
      <p>
        A change to who signs for the company on the public ledger has been requested. Everyone in the current and the
        new rule is asked to sign, and the change applies once both rules have enough signatures.
      </p>
      ${this.__currentRule
        ? html`
            <p>Now: ${summariseRule(this.__currentRule, this.__nameOf)}</p>
          `
        : nothing}
      ${this.__proposedRule
        ? html`
            <p>Proposed: ${summariseRule(this.__proposedRule, this.__nameOf)}</p>
          `
        : nothing}
    `;
  }

  renderTopicChangeTransaction() {
    return html`
      <p>
        A change to who signs the company's public ledger topic messages has been requested. Everyone who signs them now
        and everyone in the new rule is asked to approve it, and it applies once both rules have enough approvals.
      </p>
      ${this.__currentRule
        ? html`
            <p>Now: ${summariseRule(this.__currentRule, this.__nameOf)}</p>
          `
        : nothing}
      ${this.__proposedRule
        ? html`
            <p>Proposed: ${summariseRule(this.__proposedRule, this.__nameOf)}</p>
          `
        : nothing}
    `;
  }

  renderMessageSubmitTransaction() {
    if (!this.__message) return nothing;
    const parts = this.__transactions.length;
    const partsNote =
      parts > 1 && this.__view.state !== 'RESIGN_FAILED_PART'
        ? html`
            <p>This message is in ${parts} parts on the public ledger. Adding your signature signs every part.</p>
          `
        : nothing;
    if (this.__messageType === PublicLedgerMessageType.PLAIN_TEXT) {
      return html`
        <p>The below message needs your signature</p>
        ${this.__renderSignerGroups()}
        <div class="note pl-message">
          ${repeat(
            this.__messageContent,
            (line: string) => html`
              <div>${line}</div>
            `,
          )}
        </div>
        ${partsNote}
      `;
    }
    return html`
      ${this.__renderSignerGroups()}
      <p class="note pl-message">
        The message is for ${this.__message.content.collection} entity with ID: ${this.__message.content.entityId}
      </p>
      ${partsNote}
    `;
  }

  private __renderTransaction() {
    return choose(this.__signatureType, [
      [
        SignatureType.NOT_IMPLEMENTED,
        () => html`
          <p>Signature type is not implemented</p>
        `,
      ],
      [SignatureType.ACCOUNT_CHANGE, () => this.renderAccountChangeTransaction()],
      [SignatureType.TOPIC_CHANGE, () => this.renderTopicChangeTransaction()],
      [SignatureType.MESSAGE_SUBMIT, () => this.renderMessageSubmitTransaction()],
      [SignatureType.APPROVE_ALLOWANCE, () => this.__renderApproveAllowance()],
      [SignatureType.CARD_CHANGE, () => this.__renderCardChange()],
    ]);
  }

  // Round 15: a change of the automatic payment card: what signing it does, then its record's memo as a message is shown
  // (no card details, no Now / After table)
  private __renderCardChange() {
    return html`
      ${this.__renderSignerGroups()}
      <p>${cardChangeSignCopy(this.__cardChangeBy)}</p>
      ${this.__cardChangeMemo
        ? html`
            <div class="note pl-message"><div>${this.__cardChangeMemo}</div></div>
          `
        : html`
            <p class="note">${CARD_CHANGE_GONE_COPY}</p>
          `}
    `;
  }

  // #40: a stop of automatic payments: what signing it does, what stopping means, then its memo as a message is shown
  private __renderStop() {
    return html`
      ${this.__renderSignerGroups()}
      <p>${stopSignCopy(this.__stopBy)}</p>
      ${this.__stopMemo
        ? html`
            <div class="note pl-message"><div>${this.__stopMemo}</div></div>
          `
        : html`
            <p class="note">${STOP_GONE_COPY}</p>
          `}
      ${this.__stopLines.length > 0
        ? html`
            <ul>
              ${this.__stopLines.map(
                (line) => html`
                  <li>${line}</li>
                `,
              )}
            </ul>
          `
        : nothing}
    `;
  }

  // The automatic payments settings, as the server stored them; signing lets the operating account draw on the company
  // account under them
  private __renderApproveAllowance() {
    if (this.__approveKind === 'AMOUNTS') return this.__renderChangeAmounts();
    if (this.__approveKind === 'STOP') return this.__renderStop();
    const rows = approveSummaryRows(this.__proposal);
    const resign = this.__resign;
    return html`
      ${this.__renderSignerGroups()}
      ${resign && this.__resignChanged
        ? html`
            <p>${RESIGN_CHANGED_REFUSAL}</p>
          `
        : resign
          ? html`
              <p>
                Your signature puts back the allowance automatic top-ups draw on, which is missing on the public ledger.
                The settings below are your company's current ones, unchanged. It lets your company's operating account
                draw credits from its company account on the public ledger; nodeStream moves no more than the company
                target in one top-up and only as these settings allow. nodeStream pays the public ledger fee for this
                signing. Before you sign, this device checks on the public ledger that the allowance is to your
                company's own operating account.
              </p>
            `
          : html`
              <p>
                Your signature turns on automatic payments with the settings below. It lets your company's operating
                account draw credits from its company account on the public ledger; nodeStream moves no more than the
                company target in one top-up and only as these settings allow. nodeStream pays the public ledger fee for
                this signing.
              </p>
            `}
      ${rows
        ? html`
            <ns-detail-list .rows=${rows}></ns-detail-list>
            <p class="note">${APPROVE_RATE_NOTE}</p>
          `
        : html`
            <p class="note">${this.__proposalNote ?? 'The settings are loading.'}</p>
          `}
    `;
  }

  // A change of amounts (#25 phase 6, G14): the approved copy, then the live settings beside the new ones, changes marked
  // "(changed)" and the caption: approved (round 4 follow-ups, small string 5; copy walkthrough 10)
  private __renderChangeAmounts() {
    const rows = nowAfterRows(this.__liveSettings, this.__proposal);
    const rate = nowAfterRateNote(this.__proposal);
    return html`
      ${this.__renderSignerGroups()}
      <p>${CHANGE_AMOUNTS_SIGN_COPY}</p>
      ${rows
        ? html`
            <table class="now-after">
              <caption class="visually-hidden">The automatic payments settings now and after this change</caption>
              <thead>
                <tr>
                  <th scope="col"><span class="visually-hidden">Setting</span></th>
                  <th scope="col">Now</th>
                  <th scope="col">After</th>
                </tr>
              </thead>
              <tbody>
                ${rows.map(
                  (row) => html`
                    <tr class=${row.changed ? 'changed' : ''}>
                      <th scope="row">${row.label}</th>
                      <td>${row.now}</td>
                      <td>
                        ${row.after}${row.changed
                          ? html`
                              <span class="visually-hidden">(changed)</span>
                            `
                          : nothing}
                      </td>
                    </tr>
                  `,
                )}
              </tbody>
            </table>
            <p class="note">${APPROVE_RATE_NOTE}${rate ? ` ${rate}` : ''}</p>
          `
        : html`
            <p class="note">${this.__proposalNote ?? 'The settings are loading.'}</p>
          `}
    `;
  }

  private __renderExpiry() {
    const expiresAt = this.__transaction?.expiresAt;
    if (!expiresAt) return nothing;
    return html`
      <p>
        Every ${this.__isApproval ? 'approval' : 'signature'} is needed by ${renderDate(expiresAt, 'dateTime')}. After
        that, this request expires.
      </p>
    `;
  }

  private __renderResignNotice() {
    const { partNumber, failureReason } = this.__view;
    const reason = failureReason ?? ledgerResultText(this.__transaction?.resultCode) ?? 'no reason was given';
    return html`
      <p class="error-text" role="alert">
        Part ${partNumber} of this message failed on the public ledger: ${reason}. Please sign it again.
      </p>
    `;
  }

  renderPendingJob() {
    if (this.__warning) {
      return html`
        <div class="job-panel">
          <p class="error-text" role="alert">
            The signature has been submitted to the public ledger but it was unsuccessful because of the following:
          </p>
          <p class="note">${this.__warning}</p>
        </div>
      `;
    }
    if (this.__isApproval) {
      return html`
        <p slot="intro">Check the change, then approve or reject it, and complete this job.</p>
        <div class="job-panel">
          ${this.__renderTransaction()} ${this.__renderExpiry()}
          <p class="note">
            Approving doesn't sign anything on the public ledger. nodeStream applies the change once enough people have
            approved it.
          </p>
        </div>
      `;
    }
    return html`
      <p slot="intro">You need to verify the data, then add your signature or reject it, and complete this job.</p>
      <div class="job-panel">
        ${this.__view.state === 'RESIGN_FAILED_PART' ? this.__renderResignNotice() : nothing}
        ${this.__renderTransaction()} ${this.__renderExpiry()}
        ${this.__feeWaitingForTopUp
          ? html`
              <p class="note" role="status">${FEE_WAITING_FOR_TOP_UP}</p>
            `
          : nothing}
        <p class="note signing-warning">
          <img alt="" src="/images/icons/urgent.svg" />
          <span>
            By adding your signature you're signing the public ledger and authorising this data, ensure you are 100%
            happy before adding your signature and completing this job.
          </span>
        </p>
        ${this.__messageType === PublicLedgerMessageType.ENCRYPTED
          ? html`
              <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__decryptMessage()}>
                Decrypt
              </wa-button>
            `
          : nothing}
        ${this.__messageType === PublicLedgerMessageType.HASHED
          ? html`
              <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.__verifyHashedData()}>
                Verify Data
              </wa-button>
            `
          : nothing}
      </div>
    `;
  }

  private __renderRejecting() {
    return html`
      <p slot="intro">
        ${this.__isApproval
          ? 'Tell the others why you are rejecting this.'
          : 'Tell the other signatories why you are rejecting this.'}
      </p>
      <div class="job-panel">
        ${this.__renderTransaction()}
        <wa-textarea
          label="Reason for rejecting"
          required
          .value=${this.__reason}
          @input=${(e: Event) => {
            this.__reason = (e.target as HTMLTextAreaElement).value;
          }}
        ></wa-textarea>
        ${this.__reasonError
          ? html`
              <p class="error-text" role="alert">${this.__reasonError}</p>
            `
          : nothing}
        <p>
          ${this.__isApproval
            ? "Rejecting stops this change for everyone, and the topic's signatories stay as they are."
            : "Rejecting stops this for every signatory, and it won't go on the public ledger."}
          Your reason is shown to the others in nodeStream; it is not put on the public ledger.
        </p>
      </div>
    `;
  }

  private __renderRejected() {
    const { rejection } = this.__view;
    const name = this.__rejecterName || 'a signatory';
    return html`
      <div class="job-panel">
        ${this.__renderTransaction()}
        <p>
          ${this.__isApproval
            ? "This was rejected, so the topic's signatories are unchanged. You don't need to approve it."
            : "This was rejected, so it won't go on the public ledger. You don't need to sign it."}
        </p>
        <p class="note rejection-reason">
          Rejected by
          ${name}${rejection?.reason
            ? html`
                : ${rejection.reason}
              `
            : nothing}
        </p>
      </div>
    `;
  }

  private __renderEnded() {
    const { ended, cancelReason } = this.__view;
    const approval = this.__isApproval;
    let text = approval ? 'This is no longer waiting for approvals.' : 'This is no longer waiting for signatures.';
    if (ended === 'EXPIRED') {
      text = approval
        ? "This expired before everyone needed had approved it, so the topic's signatories are unchanged."
        : "This expired before everyone needed had signed, so it didn't go on the public ledger.";
    }
    if (ended === 'FAILED') {
      const reason = ledgerResultText(this.__transaction?.resultCode);
      text = `The public ledger didn't accept ${approval ? 'the change' : 'this'}${reason ? `: ${reason}` : ''}.`;
    }
    if (ended === 'CANCELLED') text = `This request was cancelled${cancelReason ? `: ${cancelReason}` : ''}.`;
    return html`
      <div class="job-panel">
        ${this.__renderTransaction()}
        <p>${text} You don't need to ${approval ? 'approve' : 'sign'} it.</p>
      </div>
    `;
  }

  render() {
    return html`
      <ns-job-layout>
        ${choose(this.__view.state, [
          ['PENDING', () => this.renderPendingJob()],
          ['RESIGN_FAILED_PART', () => this.renderPendingJob()],
          ['REJECTING', () => this.__renderRejecting()],
          [
            'ALREADY_SIGNED',
            () => html`
              <div class="job-panel">
                <p>
                  ${this.__isApproval
                    ? "You've approved this. It applies once everyone needed has approved. You can complete this job now."
                    : "You've signed this. It goes to the public ledger once everyone needed has signed. You can complete this job now."}
                </p>
              </div>
            `,
          ],
          [
            'PROCESSED_SUBMITTED',
            () => html`
              <div class="job-panel">
                ${this.__renderTransaction()}
                <p>
                  ${this.__isApproval
                    ? "This has enough approvals, so the change is being applied. You don't need to approve it."
                    : "This has enough signatures and was sent to the public ledger. You don't need to sign it."}
                </p>
              </div>
            `,
          ],
          ['PROCESSED_REJECTED', () => this.__renderRejected()],
          ['PROCESSED_ENDED', () => this.__renderEnded()],
        ])}
      </ns-job-layout>
    `;
  }

  renderActions() {
    const { state } = this.__view;
    if ((state === 'PENDING' || state === 'RESIGN_FAILED_PART') && !this.__warning) {
      return html`
        <wa-button
          variant="brand"
          appearance="outlined"
          type="button"
          ?disabled=${this.__busy}
          @click=${() => this.__startRejecting()}
        >
          Reject
        </wa-button>
        ${this.__isApproval
          ? html`
              <wa-button
                variant="brand"
                type="button"
                ?disabled=${this.__busy || !this.__ready}
                @click=${() => this.__approve()}
              >
                Approve
              </wa-button>
            `
          : html`
              <wa-button
                variant="brand"
                type="button"
                ?disabled=${this.__busy || !this.__ready}
                @click=${() => this.__sign()}
              >
                <img slot="start" alt="" src="/images/icons/sign.svg" />
                Add Signature
              </wa-button>
            `}
      `;
    }
    if (state === 'REJECTING') {
      return html`
        <wa-button
          variant="brand"
          appearance="outlined"
          type="button"
          ?disabled=${this.__busy}
          @click=${() => this.__cancelRejecting()}
        >
          Back
        </wa-button>
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__reject()}>
          Reject
        </wa-button>
      `;
    }
    if (PROCESSED_STATES.includes(state)) {
      return html`
        <wa-button variant="brand" type="button" @click=${() => this.outcome('acknowledged')}>Complete</wa-button>
      `;
    }
    return html`
      <wa-button variant="brand" type="button" @click=${() => this.outcome('signed')}>Complete</wa-button>
    `;
  }
}
